import { Link } from 'react-router-dom';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Concern = { icon: SvgIconComponent; title: string; body: string; linkTo?: string };

const concerns: Concern[] = [
  {
    icon: PercentOutlinedIcon,
    title: 'Margin per client',
    body: 'Every subscription you\'re not paying for six separate tools is margin back in the business. Consolidation isn\'t a cost-cutting exercise here — it\'s a structural improvement to the number that actually determines whether the business works.',
    linkTo: '/solutions/it-consolidation',
  },
  {
    icon: GppGoodOutlinedIcon,
    title: 'Risk exposure',
    body: 'A security incident at a client is your liability too — your access, your credential handling, your platform\'s architecture become part of the story. Tenant isolation and signed remote execution aren\'t abstractions when it\'s your name on the contract.',
    linkTo: '/security',
  },
  {
    icon: TrendingUpOutlinedIcon,
    title: 'Growth without proportional overhead',
    body: 'Adding a client shouldn\'t require adding ops headcount at the same rate. One platform with role-based access and shared context is what lets a small team take on more clients without everyone drowning.',
    linkTo: '/solutions/growing-msps',
  },
  {
    icon: SellOutlinedIcon,
    title: 'A business that\'s worth more than you alone',
    body: 'A documented, systemized operation — client history, runbooks, and procedures that live in the platform instead of your head — isn\'t just easier to run. It\'s the difference between a job and a business someone else could actually buy or run without you.',
    linkTo: '/blog/when-to-hire-your-first-tech',
  },
];

export function SolutionsMspOwners(): JSX.Element {
  useSeo({
    title: 'For MSP owners',
    description:
      'Margin, risk, and growth — the three things an MSP owner actually manages. How Nexus\'s consolidation, architecture, and role-based access map to each.',
    path: '/solutions/msp-owners',
  });

  return (
    <div>
      <PageHero
        eyebrow="For MSP owners"
        title="Margin, risk, and growth — the three things you actually manage."
        subtitle="Everything else — which ticketing tool, which RMM agent, which backup vendor — is downstream of those three. Here's how consolidating onto one platform maps directly to them."
      >
        <Reveal delay={150}>
          <div className="mt-9 flex justify-center">
            <NotifyForm />
          </div>
        </Reveal>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="container-content">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {concerns.map((c, i) => {
              const Icon = c.icon;
              const card = (
                <div className="group h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                    <Icon fontSize="small" />
                  </div>
                  <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{c.title}</h3>
                  <p className="mt-1.5 text-sm text-white/65">{c.body}</p>
                </div>
              );
              return (
                <Reveal key={c.title} delay={i * 90}>
                  {c.linkTo ? (
                    <Link to={c.linkTo} className="block no-underline">
                      {card}
                    </Link>
                  ) : (
                    card
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
              The pitch you can actually verify
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              You don&apos;t have to take a consolidation pitch on faith. The{' '}
              <Link to="/platform" className="text-nexus-purple-light no-underline hover:underline">
                platform page
              </Link>{' '}
              labels every module by what&apos;s actually true today, the{' '}
              <Link to="/security" className="text-nexus-purple-light no-underline hover:underline">
                trust center
              </Link>{' '}
              lays out the architecture underneath it, and the{' '}
              <Link to="/roadmap" className="text-nexus-purple-light no-underline hover:underline">
                roadmap
              </Link>{' '}
              shows exactly what&apos;s shipped versus still building — the same standard we&apos;d
              want if we were the one evaluating a vendor to run our own business on.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Run the numbers on your own stack.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Join the design-partner cohort and we&apos;ll walk through what consolidation
                actually changes for your specific tool stack and client count.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
